import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Formik, Form } from 'formik';
import { useAuth } from 'hooks/useAuth';
import { Box, Grid, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { manualBankFormStyles } from '../style/style';
import { identifyUser, trackEvent } from 'utils/mixpanel';

import {
    startTour,
    TOUR_TYPES,
    goToNextTour,
    completeTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    selectIsCompleted,
    selectTutorialPreference,
} from 'store/slices/tour.slice';
import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';
import { manualBankFormSteps } from '../utils/transactionsTutorials';
import { createManualBankAccount } from 'store/services/bank.Service';
import { bankAccountTypes, bankAccountSubtypes } from '../utils/bankAccountTypes';
import { Modal, TextField, Typography, Autocomplete, SnackBarAlert } from 'shared';
import { manualBankFormSchema, manualBankFormInitialValues } from '../utils/manualBankFormSchema';

const tourId = TOUR_TYPES.BANK_ACCOUNT;
const nextTourId = TOUR_TYPES.BANK_UPLOAD;

const ManualBankForm = ({ type, open, handleCloseModal, handleManualBankSuccess }) => {
    const classes = manualBankFormStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user: { data: userData } } = useAuth();
    const companyData = useSelector((state) => state.company.data);
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const nextTourStatus = useSelector((state) => selectTourStatus(state, nextTourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const isNextTourCompleted = useSelector((state) => selectIsCompleted(state, nextTourId));

    const [tourRun, setTourRun] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    const handleStepBefore = (idx) => {
        setCurrentStepIndex(idx);
        dispatch(updateTourStep({ tourId, stepIndex: idx }));
    };

    const handleTourFinish = () => {
        setTourRun(false);

        if (tutorialPreference === 'skipped' || isSkipped) {
            dispatch(completeTour({ tourId, skipSaving: false }));
            return;
        }

        dispatch(completeTour({ tourId, skipSaving: false }));

        // if (!isNextTourCompleted) {
        //     dispatch(
        //         goToNextTour({
        //             currentTourId: tourId,
        //             nextTourId: TOUR_TYPES.BANK_UPLOAD,
        //         }),
        //     );
        //     navigate('/upload-transactions');
        // } else {
        //     dispatch(completeTour({ tourId }));
        // }

        // handleCloseModal();
    };

    const handleSubmit = async (values, { setFieldError }) => {
        try {
            setButtonLoading(true);
            const { type, openingBalanceDate, detailType } = values;
            const data = {
                ...values,
                type: type.value,
                detailType: detailType.value,
                openingBalanceDate: dayjs(openingBalanceDate).format('YYYY-MM-DD'),
                company_id: companyData.id,
            };
            const response = await createManualBankAccount(data);

            handleManualBankSuccess(response?.message);

            identifyUser(userData.email.toLowerCase(), { $email: userData.email });
            trackEvent('Bank Account', {
                source: "Manual",
            });
            if (tutorialPreference === 'skipped' || isSkipped) {
                setTourRun(false);
                return;
            }
            if (!nextTourStatus?.completed) {
                dispatch(
                    goToNextTour({
                        currentTourId: tourId,
                        nextTourId: nextTourId,
                    }),
                );
                handleManualBankSuccess(response?.message);
                setTimeout(() => {
                    navigate('/transactions/upload-transactions');
                }, 1000);
            } else {
                dispatch(completeTour({ tourId }));
                handleManualBankSuccess(response?.message);
            }

            // handleManualBankSuccess(response?.message);
        } catch (error) {
            const resp = error.response;

            if (resp?.status === 400 && resp.data) {
                const errs = resp.data;

                if (errs.accountNumber) {
                    setFieldError('accountNumber', errs.accountNumber[0]);
                }

                if (errs.name) {
                    setFieldError('name', errs.name[0]);
                }
                return;
            }

            handleErrorAlert(setAlertData, resp?.data?.message ?? 'Something went wrong…');
        } finally {
            setButtonLoading(false);
        }
    };

    const handleClose = () => {
        setTourRun(false);
        handleCloseModal();
    };

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        if (open) {
            if (tutorialPreference === 'skipped' || isSkipped) {
                setTourRun(false);
            }
            const timer = setTimeout(() => {
                if (manualRestart) {
                    setCurrentStepIndex(0);
                    dispatch(startTour({ tourId }));
                    setTourRun(true);
                } else if (!tourStatus?.completed && !manualRestart) {
                    if (tutorialPreference === 'skipped' || isSkipped) {
                        setTourRun(false);
                        return;
                    }
                    setTourRun(true);
                    if (tourStatus?.started) {
                        setCurrentStepIndex(tourStatus.currentStep);
                    } else {
                        dispatch(startTour({ tourId }));
                        setCurrentStepIndex(0);
                    }
                } else {
                }
            }, 500);

            return () => clearTimeout(timer);
        } else {
            setTourRun(false);
        }
    }, [open, dispatch, tourStatus?.completed, tourStatus?.manuallyStarted]);

    return (
        <Modal
            fullWidth
            open={open}
            title={`${type} Bank Account`}
            actionBtnTitle={<Typography text={type === 'Add' ? 'Create' : 'Update'} />}
            handleClose={handleCloseModal}
        >
            <JoyrideTour
                steps={manualBankFormSteps}
                run={tourRun}
                continuous={true}
                onStepBefore={handleStepBefore}
                onFinish={handleTourFinish}
                showSkipButton={true}
                showProgress={true}
                stepIndex={currentStepIndex}
                isModal={true}
                showBeacon={true}
                spotlightClicks={true}
                callback={(event) => {
                    if (event.action === 'skip' || event.type === 'tour:end') {
                        setTourRun(false);
                        dispatch(completeTour({ tourId, skipSaving: false }));
                        handleTourFinish();
                    }
                }}
            />
            <Formik
                initialValues={manualBankFormInitialValues()}
                validationSchema={manualBankFormSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Box width="100%" gap={2} mt={1} border="none">
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="name"
                                        placeholder="Name"
                                        value={values.name}
                                        onChange={(e) => setFieldValue('name', e.target.value)}
                                        error={touched.name && !!errors.name}
                                        helperText={touched.name && errors.name ? errors.name : ''}
                                        data-tour="bank-form-name"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box data-tour="bank-form-type" id="bank-form-type-container">
                                        <Autocomplete
                                            options={bankAccountTypes}
                                            labelKey="label"
                                            valueKey="value"
                                            placeholder="Type"
                                            name="type"
                                            value={values.type}
                                            onChange={(e, value) => {
                                                setFieldValue('type', value);
                                                setFieldValue('detailType', null);
                                            }}
                                            isSearchField={false}
                                            disableClearable
                                            error={touched.type && !!errors.type}
                                            helperText={touched.type && errors.type}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box
                                        data-tour="bank-form-detail-type"
                                        id="bank-form-detail-type-container"
                                    >
                                        <Autocomplete
                                            disabled={!values.type}
                                            options={bankAccountSubtypes[values.type?.value]}
                                            labelKey="label"
                                            valueKey="value"
                                            placeholder="Detail Type"
                                            name="detailType"
                                            value={values.detailType}
                                            onChange={(e, value) =>
                                                setFieldValue('detailType', value)
                                            }
                                            isSearchField={false}
                                            disableClearable
                                            error={touched.detailType && !!errors.detailType}
                                            helperText={touched.detailType && errors.detailType}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="maskNumber"
                                        placeholder="Mask Number"
                                        value={values.maskNumber}
                                        onChange={(e) =>
                                            setFieldValue('maskNumber', e.target.value)
                                        }
                                        error={touched.maskNumber && !!errors.maskNumber}
                                        helperText={touched.maskNumber && errors.maskNumber}
                                        data-tour="bank-form-mask-number"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" marginTop={3} gap={2}>
                            <Button onClick={handleCloseModal}>Cancel</Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                loading={buttonLoading}
                                data-tour="bank-form-submit"
                            >
                                {type === 'Add' ? 'Create' : 'Update'}
                            </LoadingButton>
                        </Box>
                    </Form>
                )}
            </Formik>

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Modal>
    );
};

export default ManualBankForm;
